import { globalConfirm } from '../components/ConfirmDialog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppShell from '../components/layout/AppShell';
import { LoadingSpinner, EmptyState } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend, CartesianGrid
} from 'recharts';
import * as XLSX from 'xlsx';
import api, { getApiErrorMessage } from '../services/api';
import TypeAheadLookup from '../components/TypeAheadLookup';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';
import { SiteSelector } from '../components/SiteSelector';
import { toast } from 'sonner';
import { AttachmentsPanel, TermsPanel, NotesPanel, CostCentreSplitsPanel } from '../components/shared/DocumentPanels';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { useExpenditureLabels, useDecimalFormat } from '../contexts/ExpenditureLabelsContext';
import {
  ChevronDown, ChevronLeft, ChevronRight, X, Plus, Trash2, ChevronsDown,
  Layers, FileText, CheckSquare, Split, Paperclip,
  ScrollText, StickyNote, Package, Activity, Save,
  TrendingUp, BarChart2, Upload, Search, Wand2, Download,
  RefreshCw, Edit, FileSpreadsheet, ListOrdered, AlignJustify, ShoppingCart,
  AlertTriangle, ArrowDownToLine
} from 'lucide-react';

// Import refactored components and constants
import {
  BACKEND_URL,
  SHIPPING_METHODS,
  INCOTERMS_LIST,
  STATUS_STYLES,
  EDITABLE_STATUSES,
  formatDate as fmt,
  formatTimestamp as fmtTs
} from './Requisitions/constants';
import { BudgetChartModal } from './Requisitions/modals';

// Extracted components
import HeaderMenu from './Requisitions/components/HeaderMenu';
import SupplierInlineSearch from './Requisitions/components/SupplierInlineSearch';
import LineItemDetailEditor from './Requisitions/components/LineItemDetailEditor';
import LineItemForm from './Requisitions/components/LineItemForm';
import IconSidebar from './Requisitions/components/IconSidebar';
import MainDetailsPanel from './Requisitions/components/MainDetailsPanel';
import AddCatalogItemModal from './Requisitions/modals/AddCatalogItemModal';
import AddPunchoutModal from './Requisitions/modals/AddPunchoutModal';
import ItemSourcingModal from './Requisitions/modals/ItemSourcingModal';
import UpdateAllModal from './Requisitions/modals/UpdateAllModal';


// ── 9-Icon Sidebar ─────────────────────────────────────────────────────────────────────────
const PANEL_TABS = [
  { id: 'main',         icon: Layers,       label: 'Main Details',       badgeKey: 'line_items' },
  { id: 'inventory',    icon: Package,       label: 'Inventory Check',    badgeKey: null },
  { id: 'approvers',    icon: CheckSquare,   label: 'Approvers',          badgeKey: 'waiting_approvers' },
  { id: 'splits',       icon: Split,         label: 'Cost Centre Splits', badgeKey: 'cost_centre_splits' },
  { id: 'attachments',  icon: Paperclip,     label: 'Attached Documents', badgeKey: 'attachments' },
  { id: 'tnc',          icon: ScrollText,    label: 'Terms & Conditions', badgeKey: 'terms_conditions' },
  { id: 'notes',        icon: StickyNote,    label: 'Notes',              badgeKey: 'notes' },
  { id: 'orders',       icon: Package,       label: 'Linked Orders',      badgeKey: 'linked_orders' },
  { id: 'events',       icon: Activity,      label: 'Event Log',          badgeKey: 'events' },
];


export default function RequestDetailPage() {
  const { requestNumber } = useParams();
  const navigate = useNavigate();
  const { user, systemSettings } = useAuth();
  const tz = systemSettings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const fmtTz = (d) => fmtTs(d, tz);
  const fmtDateTz = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }); } catch { return d; } };
  const { labels: expLabels } = useExpenditureLabels();
  const { fmtVal, fmtTotal } = useDecimalFormat();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePanel, setActivePanel] = useState('main');
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reqEvtSort, handleReqEvtSort] = useSortState('event_date', 'desc');
  const [cancelReason, setCancelReason] = useState('');
  const [budgetModal, setBudgetModal] = useState(null); // {code, label}
  const [events, setEvents] = useState([]);
  const [segments, setSegments] = useState([]);
  // Edit states
  const [headerEdit, setHeaderEdit] = useState({});
  const [addingItem, setAddingItem] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [newNote, setNewNote] = useState({ notes: '', privacy: 'Private' });
  const [newSplit, setNewSplit] = useState({ cost_centre_code: '', cost_centre_description: '', split_percentage: 0 });
  const [tcSearch, setTcSearch] = useState('');
  const [allTcs, setAllTcs] = useState([]);
  const [mandatoryFields, setMandatoryFields] = useState({ header: [], line: [] });
  const docRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqRes, segsRes, tcsRes, mfRes] = await Promise.all([
        api.get(`/api/requisitions/${requestNumber}`),
        api.get('/api/system/account-code-segments'),
        api.get('/api/data/terms-conditions', { params: { page_size: 100 } }),
        api.get('/api/admin/mandatory-fields').catch(() => ({ data: { mandatory_fields: { header: [], line: [] } } })),
      ]);
      setReq(reqRes.data);
      setHeaderEdit(reqRes.data);
      setSegments(segsRes.data);
      setAllTcs((tcsRes.data.data || []));
      setMandatoryFields(mfRes.data.mandatory_fields || { header: [], line: [] });
    } catch (err) {
      if (err?.response?.status >= 500) {
        toast.error('Failed to load requisition');
      }
    }
    setLoading(false);
  }, [requestNumber]);

  const loadEvents = useCallback(async () => {
    try { const res = await api.get(`/api/requisitions/${requestNumber}/events`); setEvents(res.data); } catch {}
  }, [requestNumber]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (activePanel === 'events') loadEvents(); }, [activePanel, loadEvents]);

  const canEdit = req && EDITABLE_STATUSES.has(req.status);
  const isMandatory = (field) => mandatoryFields.header?.includes(field);
  const isLineMandatory = (field) => mandatoryFields.line?.includes(field);
  const mandatoryLabel = (field, label) => (
    <p className={`text-xs font-medium ${isMandatory(field) ? 'text-red-500' : 'text-muted-foreground'}`}>{label}{isMandatory(field) ? ' *' : ''}</p>
  );

  const saveHeader = async () => {
    // Validate mandatory fields
    const missing = [];
    for (const field of mandatoryFields.header || []) {
      const fieldLabels = { department_code: 'Department Code', ref_num: 'Reference Number', request_description: 'Request Description', delivery_to: 'Delivery To', delivery_code: 'Delivery Code', document_type: 'Document Type', category_code: 'Category Code', instructions: 'Instructions', incoterms: 'Incoterms', ship_via: 'Ship Via', account: 'Account', restaurant: 'Restaurant' };
      if (!headerEdit[field]) missing.push(fieldLabels[field] || field);
    }
    if (missing.length > 0) {
      toast.error(`Missing mandatory fields: ${missing.join(', ')}`);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/api/requisitions/${requestNumber}`, headerEdit);
      toast.success('Saved');
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Save failed')); }
    setSaving(false);
  };

  const h = (k, v) => setHeaderEdit(p => ({ ...p, [k]: v }));

  const addLineItem = async (item) => {
    try {
      await api.post(`/api/requisitions/${requestNumber}/line-items`, item);
      toast.success('Item added');
      setAddingItem(false);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  const updateLineItem = async (id, item) => {
    try {
      const res = await api.put(`/api/requisitions/${requestNumber}/line-items/${id}`, item);
      toast.success('Item updated');
      // Don't close editor until after reload to prevent race condition
      await load();
      setEditingItemId(null);
    } catch (e) {
      if (e?.code === 'ERR_CANCELED') return; // Request cancelled by navigation
      toast.error(getApiErrorMessage(e, 'Save failed'));
    }
  };

  const deleteLineItem = async (id) => {
    if (!await globalConfirm('Delete this line item?')) return;
    try {
      await api.delete(`/api/requisitions/${requestNumber}/line-items/${id}`);
      toast.success('Deleted');
      load();
    } catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Failed'); }
  };

  const addNote = async () => {
    if (!newNote.notes.trim()) return;
    try {
      await api.post(`/api/requisitions/${requestNumber}/notes`, newNote);
      setNewNote({ notes: '', privacy: 'Private' });
      toast.success('Note added');
      load();
    } catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Failed'); }
  };

  const deleteNote = async (id) => {
    try { await api.delete(`/api/requisitions/${requestNumber}/notes/${id}`); load(); } catch {}
  };

  const saveSplits = async () => {
    try {
      await api.put(`/api/requisitions/${requestNumber}/cost-centre-splits`, { splits: req.cost_centre_splits || [] });
      toast.success('Splits saved');
      load();
    } catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Failed'); }
  };

  const addSplit = () => {
    if (!newSplit.cost_centre_code) return;
    const splits = [...(req.cost_centre_splits || []), { ...newSplit }];
    setReq(p => ({ ...p, cost_centre_splits: splits }));
    setNewSplit({ cost_centre_code: '', cost_centre_description: '', split_percentage: 0 });
  };

  const removeSplit = (idx) => setReq(p => ({ ...p, cost_centre_splits: p.cost_centre_splits.filter((_, i) => i !== idx) }));

  const updateSplitPct = (idx, pct) => setReq(p => ({
    ...p,
    cost_centre_splits: p.cost_centre_splits.map((s, i) => i === idx ? { ...s, split_percentage: parseFloat(pct) || 0 } : s)
  }));

  const toggleTc = (tc) => {
    const existing = (req.terms_conditions || []);
    const found = existing.find(t => t.code === tc.code);
    let updated;
    if (found) { updated = existing.filter(t => t.code !== tc.code); }
    else { updated = [...existing, { code: tc.code, outline: tc.outline }]; }
    setReq(p => ({ ...p, terms_conditions: updated }));
  };

  const saveTcs = async () => {
    try {
      await api.put(`/api/requisitions/${requestNumber}/terms-conditions`, { terms_conditions: req.terms_conditions || [] });
      toast.success('T&Cs saved');
      load();
    } catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Failed'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('description', ''); fd.append('privacy', 'Private');
    try {
      await api.post(`/api/requisitions/${requestNumber}/attachments`, fd);
      toast.success('File uploaded');
      load();
    } catch (err) { if (err?.code !== 'ERR_CANCELED' && err?.response?.status !== 401 && err?.response?.status !== 403) toast.error('Upload failed'); }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer?.files || []);
    for (const file of files) {
      const fd = new FormData(); fd.append('file', file); fd.append('description', ''); fd.append('privacy', 'Private');
      try {
        await api.post(`/api/requisitions/${requestNumber}/attachments`, fd);
      } catch {}
    }
    if (files.length) { toast.success(`${files.length} file(s) uploaded`); load(); }
  };

  const submitReq = async () => {
    try {
      await api.post(`/api/requisitions/${requestNumber}/submit`);
      toast.success('Requisition submitted for approval');
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Submit failed')); }
  };

  const createPO = async () => {
    const suppliers = new Set((req.line_items || []).map(it => it.supplier_code || '').filter(Boolean));
    const msg = suppliers.size > 1
      ? `This will create ${suppliers.size} Purchase Orders (one per supplier) from this requisition. Continue?`
      : 'Create a Purchase Order from this requisition?';
    if (!await globalConfirm(msg)) return;
    try {
      const res = await api.post(`/api/approvals/create-po/${requestNumber}`);
      toast.success(res.data.message);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to create PO')); }
  };


  const copyReq = async () => {
    try {
      setHeaderMenuOpen(false);
      const toastId = toast.loading('Copying requisition...');
      const res = await api.post(`/api/requisitions/${requestNumber}/copy`);
      toast.dismiss(toastId);
      toast.success(`Copied as ${res.data.request_number}`);
      navigate(`/requests/${res.data.request_number}`);
    } catch (err) { 
      toast.dismiss();
      toast.error(err?.response?.data?.detail || 'Copy failed — please try again');
    }
  };

  const cancelReq = async () => {
    if (!cancelReason.trim()) { toast.error('Cancellation reason is required'); return; }
    try {
      const r = await api.post(`/api/approvals/cancel/${requestNumber}`, { reason: cancelReason });
      toast.success(r.data.message);
      setCancelDialogOpen(false);
      setCancelReason('');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Cancel failed'); }
  };

  if (loading) return <AppShell><div className="py-24"><LoadingSpinner size="lg" /></div></AppShell>;
  if (!req) return <AppShell><div className="py-24"><EmptyState title="Requisition not found" /></div></AppShell>;

  const totalSplitPct = (req.cost_centre_splits || []).reduce((s, c) => s + (parseFloat(c.split_percentage) || 0), 0);
  const netTotal = req.request_net_total || 0;

  return (
    <AppShell>
      {/* Blue header bar */}
      <div className="-mx-4 md:-mx-6 -mt-5 px-4 md:px-6 py-3 mb-4 border-b" style={{ backgroundColor: '#dce8f5' }}>
        {/* Title row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 relative">
            <button
              onClick={() => setHeaderMenuOpen(v => !v)}
              className="flex items-center gap-1.5 font-bold text-base hover:opacity-80"
              aria-label="Request menu" aria-expanded={headerMenuOpen}
            >
              <ChevronsDown className="h-5 w-5 text-orange-500" strokeWidth={3} /> Requisition
            </button>
            {headerMenuOpen && (
              <HeaderMenu requestNumber={requestNumber} status={req.status} onCopy={copyReq} onCancel={() => setCancelDialogOpen(true)} onClose={() => setHeaderMenuOpen(false)} />
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => navigate('/requests')} aria-label="Back to list">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" aria-label="Next"><ChevronRight className="h-4 w-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => navigate('/requests')} aria-label="Close" className="text-red-600">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Status strip */}
        <div className="grid grid-cols-5 gap-3 mt-2">
          <div>
            <p className="text-[10px] text-muted-foreground">Request Number</p>
            <p className="text-sm font-semibold">{req.request_number || '—'}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Request Status</p>
            <p className={`text-sm font-semibold ${(STATUS_STYLES[req.status] || '') + ' px-1 rounded'}`}>{req.status || '—'}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Entry Date</p>
            <p className="text-sm font-semibold">{req.entry_date ? fmtDateTz(req.entry_date) : '—'}</p>
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground">Rule Approval Status</p>
            <p className="text-sm font-semibold">{req.rule_approval_status || '—'}</p>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <p className="text-[10px] text-muted-foreground">Originator's Name</p>
              <p className="text-sm font-semibold">{req.originator_name || '—'}</p>
            </div>
            {req.status === 'APPROVED' && !req.order_number && (() => {
              const suppliers = new Set((req.line_items || []).map(it => it.supplier_code || '').filter(Boolean));
              const isPlural = suppliers.size > 1;
              return (
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white flex-shrink-0" onClick={createPO} data-testid="create-po-btn">
                  <ShoppingCart className="h-3.5 w-3.5 mr-1" /> {isPlural ? `Create ${suppliers.size} POs` : 'Create PO'}
                </Button>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Rejected/Cancelled banner */}
      {req.status === 'REJECTED' && (
        <div className="border border-red-200 bg-red-50 rounded-xl px-4 py-3 mb-4">
          <p className="text-sm font-semibold text-red-700">Requisition Rejected</p>
          <p className="text-xs text-red-600 mt-0.5">Rejected by {req.rejected_by || 'Unknown'}{req.rejection_notes ? `: ${req.rejection_notes}` : ''}. This document is read-only. You may copy it to create a new requisition.</p>
        </div>
      )}
      {req.status === 'CANCELLED' && (
        <div className="border border-gray-300 bg-gray-50 rounded-xl px-4 py-3 mb-4">
          <p className="text-sm font-semibold text-gray-700">Requisition Cancelled</p>
          <p className="text-xs text-gray-600 mt-0.5">{req.cancellation_reason || 'No reason provided'}. This document is read-only. You may copy it to create a new requisition.</p>
        </div>
      )}

      {/* Request Header (collapsible) */}
      <div className="border rounded-xl mb-4">
        <button
          onClick={() => setHeaderCollapsed(v => !v)}
          className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/30 rounded-t-xl text-sm font-semibold"
          aria-expanded={!headerCollapsed}
        >
          <span className="flex items-center gap-2"><ChevronDown className={`h-4 w-4 transition-transform ${headerCollapsed ? '-rotate-90' : ''}`} /> Requisition Header</span>
          <span className="text-sm font-bold text-primary">Requisition Net Total {fmtTotal(netTotal)} (USD)</span>
        </button>
        {!headerCollapsed && (
          <div className="px-4 py-4">
            <div className="grid grid-cols-5 gap-4 mb-3">
              <div className="space-y-1">
                {mandatoryLabel('ref_num', 'Ref Num')}
                {canEdit ? <Input value={headerEdit.ref_num||''} onChange={e => h('ref_num',e.target.value)} className="h-8 text-sm" /> : <p className="text-sm">{req.ref_num||'—'}</p>}
              </div>
              <div className="space-y-1">
                {mandatoryLabel('request_description', 'Request Description')}
                {canEdit ? <Input value={headerEdit.request_description||''} onChange={e => h('request_description',e.target.value)} className="h-8 text-sm" /> : <p className="text-sm">{req.request_description||'—'}</p>}
              </div>
              <div className="space-y-1">
                {mandatoryLabel('delivery_to', 'Delivery To')}
                {canEdit ? <Input value={headerEdit.delivery_to||''} onChange={e => h('delivery_to',e.target.value)} className="h-8 text-sm" /> : <p className="text-sm">{req.delivery_to||'—'}</p>}
              </div>
              <div className="space-y-1 col-span-1">
                {mandatoryLabel('instructions', 'Instructions')}
                {canEdit ? <Textarea value={headerEdit.instructions||''} onChange={e => h('instructions',e.target.value)} rows={2} className="text-xs" /> : <p className="text-xs">{req.instructions||'—'}</p>}
              </div>
              <div className="space-y-1">
                {mandatoryLabel('document_type', 'Document Type')}
                {canEdit ? <TypeAheadLookup endpoint="/api/data/document-types" valueField="code" labelField="description" value={headerEdit.document_type||''} displayValue={headerEdit.document_type||''} placeholder="Search document types..." onChange={(code)=>h('document_type',code)} /> : <p className="text-sm">{req.document_type||'—'}</p>}
              </div>
            </div>
            <div className="grid grid-cols-5 gap-4 mb-3">
              <div className="space-y-1">
                {mandatoryLabel('delivery_code', 'Delivery Code')}
                {canEdit ? <TypeAheadLookup endpoint="/api/data/delivery-points" valueField="code" labelField="name" value={headerEdit.delivery_code||''} displayValue={headerEdit.delivery_code||''} placeholder="Search delivery points..." onChange={(code, record) => {
                  h('delivery_code', code);
                  if (record) {
                    const addr = [record.name, record.address, record.city, record.state_province, record.zip_postal, record.country].filter(Boolean).join(', ');
                    h('delivery_to', addr);
                    if (record.instructions) h('instructions', record.instructions);
                  }
                }} /> : <p className="text-sm">{req.delivery_code||'—'}</p>}
              </div>
              <div className="space-y-1">
                {mandatoryLabel('category_code', 'Category Code')}
                {canEdit ? <TypeAheadLookup endpoint="/api/data/categories" valueField="code" labelField="description" value={headerEdit.category_code||''} displayValue={headerEdit.category_code||''} placeholder="Search categories..." onChange={(code)=>h('category_code',code)} /> : <p className="text-sm">{req.category_code||'—'}</p>}
              </div>
              {/* Header Expenditure Codes (exp_code_1=Account, exp_code_2=Restaurant, exp_code_3=Department Code) */}
              {[
                { key: 'account', label: expLabels.exp_code_1, segment: 1, mandatoryKey: 'account' },
                { key: 'restaurant', label: expLabels.exp_code_2, segment: 2, mandatoryKey: 'restaurant' },
                { key: 'department_code', label: expLabels.exp_code_3, segment: 3, mandatoryKey: 'department_code' },
              ].map(ec => {
                const val = req[ec.key] || '';
                return (
                  <div key={ec.key} className="space-y-1">
                    <div className="flex items-center gap-1">
                      {mandatoryLabel(ec.mandatoryKey, ec.label)}
                      {val && (
                        <button onClick={() => setBudgetModal({ code: val, label: ec.label, segment: ec.segment })} className="hover:opacity-70" title="View budget chart">
                          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><rect x="3" y="12" width="4" height="9" rx="1" fill="#2563eb"/><rect x="10" y="7" width="4" height="14" rx="1" fill="#16a34a"/><rect x="17" y="3" width="4" height="18" rx="1" fill="#d97706"/></svg>
                        </button>
                      )}
                    </div>
                    {canEdit ? (
                      <TypeAheadLookup
                        endpoint={`/api/account-codes/${ec.segment}`}
                        valueField="code" labelField="description"
                        value={headerEdit[ec.key]||''} displayValue={headerEdit[ec.key]||''}
                        placeholder={`Search ${ec.label}...`}
                        onChange={(code)=>h(ec.key,code)}
                      />
                    ) : <p className="text-sm">{val||'—'}</p>}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-5 gap-4">
              <div className="space-y-1">{mandatoryLabel('incoterms', 'Incoterms')}{canEdit ? (<select value={headerEdit.incoterms||''} onChange={e=>h('incoterms',e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm"><option value="">--</option>{INCOTERMS_LIST.map(i=><option key={i}>{i}</option>)}</select>) : <p className="text-sm">{req.incoterms||'—'}</p>}</div>
              <div className="space-y-1">{mandatoryLabel('ship_via', 'Ship Via')}{canEdit ? (<select value={headerEdit.shipping_method||''} onChange={e=>h('shipping_method',e.target.value)} className="h-8 w-full rounded-lg border border-input bg-background px-2 text-sm"><option value="">--</option>{SHIPPING_METHODS.map(s=><option key={s}>{s}</option>)}</select>) : <p className="text-sm">{req.shipping_method||'—'}</p>}</div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Budget</p>
                {canEdit ? (
                  <select value={headerEdit.budget_status || req.budget_status || 'IN_BUDGET'} onChange={e => h('budget_status', e.target.value)}
                    className={`h-8 w-full rounded-lg border px-2 text-sm font-semibold ${
                      (headerEdit.budget_status || req.budget_status) === 'IN_BUDGET' ? 'border-green-300 bg-green-50 text-green-700' :
                      (headerEdit.budget_status || req.budget_status) === 'OUT_OF_BUDGET' ? 'border-amber-300 bg-amber-50 text-amber-700' :
                      'border-red-300 bg-red-50 text-red-700'
                    }`}>
                    <option value="IN_BUDGET">In Budget</option>
                    <option value="OUT_OF_BUDGET">Out of Budget</option>
                    <option value="OVER_BUDGET">Over Budget</option>
                  </select>
                ) : (
                  <p className={`text-sm font-semibold ${
                    req.budget_status === 'IN_BUDGET' ? 'text-green-600' :
                    req.budget_status === 'OUT_OF_BUDGET' ? 'text-amber-600' :
                    'text-red-600'
                  }`}>{req.budget_status === 'IN_BUDGET' ? 'In Budget' : req.budget_status === 'OUT_OF_BUDGET' ? 'Out of Budget' : 'Over Budget'}</p>
                )}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Request Carbon Emissions</p>
                <p className="text-sm">{((req.line_items || []).reduce((sum, li) => sum + (parseFloat(li.co2e) || 0), 0)).toLocaleString(undefined, {maximumFractionDigits: 1})} (CO₂e)</p>
              </div>
            </div>
            {canEdit && (
              <div className="mt-3 flex justify-start">
                <Button size="sm" onClick={saveHeader} disabled={saving}><Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save Header'}</Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main content: icon sidebar + panels */}
      <div className="flex border rounded-xl overflow-hidden min-h-[400px]">
        <IconSidebar activePanel={activePanel} onSelect={setActivePanel} counts={req.counts || {}} tabs={PANEL_TABS}
          alerts={{ approvers: req.approval_needs_recalc }} />

        <div className="flex-1 p-4 overflow-auto">


          {/* Panel 1: Main Details */}
          {activePanel === 'main' && (
            <MainDetailsPanel
              req={req}
              canEdit={canEdit}
              requestNumber={requestNumber}
              addingItem={addingItem}
              setAddingItem={setAddingItem}
              editingItemId={editingItemId}
              setEditingItemId={setEditingItemId}
              addLineItem={addLineItem}
              updateLineItem={updateLineItem}
              deleteLineItem={deleteLineItem}
              submitReq={submitReq}
              onReload={load}
              netTotal={netTotal}
              segments={segments}
              mandatoryLineFields={mandatoryFields.line || []}
            />
          )}

          {/* Inventory Check Panel */}
          {activePanel === 'inventory' && (
            <InventoryCheckPanel lineItems={req.line_items || []} requestNumber={requestNumber} onReload={load} />
          )}

          {/* Panel 2: Approval Rules */}

          {/* Panel 3: Approvers */}
          {activePanel === 'approvers' && (
            <ErrorBoundary fallback={<div className="p-4 border rounded-lg bg-red-50 text-red-700 text-sm">Approvers panel failed to load. Please refresh the page.</div>}>
              <ApproversPanel req={req} requestNumber={requestNumber} user={user} onReload={load} canEdit={canEdit} submitReq={submitReq} fmtTz={fmtTz} />
            </ErrorBoundary>
          )}

          {/* Panel 4: Cost Centre Splits */}
          {activePanel === 'splits' && (
            <CostCentreSplitsPanel docType="requisitions" docNumber={requestNumber} netTotal={netTotal} fmtTz={fmtTz} onUpdate={load} />
          )}


          {/* Panel 5: Attached Documents */}
          {/* Attachments */}
          {activePanel === 'attachments' && (
            <AttachmentsPanel docType="requisitions" docNumber={requestNumber} canEdit={true} onReload={load} fmtTz={fmtTz} />
          )}

          {/* Terms & Conditions */}
          {activePanel === 'tnc' && (
            <TermsPanel docType="requisitions" docNumber={requestNumber} canEdit={true} onReload={load} fmtTz={fmtTz} />
          )}

          {/* Notes */}
          {activePanel === 'notes' && (
            <NotesPanel docType="requisitions" docNumber={requestNumber} fmtTz={fmtTz} />
          )}

          {/* Linked Orders */}
          {activePanel === 'orders' && (
            <div>
              <h3 className="font-bold text-primary mb-3">Linked Orders</h3>
              {(req.linked_orders?.length > 0 || req.order_number) ? (
                <div className="space-y-2">
                  {(req.linked_orders?.length > 0 ? req.linked_orders : [req.order_number]).map(po => (
                    <div key={po} className="border rounded-lg p-3 hover:bg-accent/20 cursor-pointer" onClick={() => navigate(`/orders/${po}`)}>
                      <p className="text-sm font-mono text-primary font-bold">{po}</p>
                      <p className="text-xs text-muted-foreground">Created from this requisition</p>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState title="No linked orders" description="Purchase orders created from this requisition will appear here." />
              )}
            </div>
          )}

          {/* Panel 9: Event Log (Full Audit Trail) */}
          {activePanel === 'events' && (
            <div>
              <h3 className="font-bold text-primary mb-3">Document Audit Trail</h3>
              {events.length === 0 ? <EmptyState title="No events yet" /> : (
                <table className="w-full text-xs">
                  <thead><tr className="bg-muted/50 border-b">
                    <SortableTableHeader label="Date & Time" field="event_date" sortState={reqEvtSort} onSort={handleReqEvtSort} />
                    <SortableTableHeader label="User" field="user" sortState={reqEvtSort} onSort={handleReqEvtSort} />
                    <SortableTableHeader label="Event" field="event" sortState={reqEvtSort} onSort={handleReqEvtSort} />
                    <SortableTableHeader label="IP Address" field="ip_address" sortState={reqEvtSort} onSort={handleReqEvtSort} className="text-right" />
                  </tr></thead>
                  <tbody>
                    {sortData(events, reqEvtSort.field, reqEvtSort.dir).map((e, i) => (
                      <tr key={i} className="border-b hover:bg-accent/20">
                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap font-mono">{fmtTz(e.event_date)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{e.user}</td>
                        <td className="px-3 py-2">
                          <p>{e.event}</p>
                          {e.details?.changed_fields && (
                            <details className="mt-1">
                              <summary className="text-[10px] text-primary cursor-pointer">View changes ({e.details.changed_fields.length})</summary>
                              <ul className="mt-1 text-[10px] text-muted-foreground space-y-0.5 pl-3">
                                {e.details.changed_fields.map((f, j) => <li key={j}>{f}</li>)}
                              </ul>
                            </details>
                          )}
                          {e.details?.approvers && (
                            <p className="text-[10px] text-muted-foreground mt-0.5">Approvers: {e.details.approvers.join(', ')}</p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground font-mono whitespace-nowrap">{e.ip_address || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Budget Chart Modal */}
      {budgetModal && (
        <BudgetChartModal open={!!budgetModal} onClose={() => setBudgetModal(null)} code={budgetModal.code} label={budgetModal.label} segment={budgetModal.segment} />
      )}

      {/* Cancel Request Dialog */}
      {cancelDialogOpen && (
        <Dialog open onOpenChange={() => setCancelDialogOpen(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="text-red-600">Cancel Requisition</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">This will cancel requisition <strong>{requestNumber}</strong>. The document will become read-only but can be copied to create a new request.</p>
            <div className="space-y-2 mt-3">
              <Label className="text-sm font-medium">Cancellation Reason *</Label>
              <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={3} placeholder="Please provide a reason for cancellation..." className="text-sm" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => setCancelDialogOpen(false)}>Back</Button>
              <Button variant="destructive" size="sm" onClick={cancelReq} disabled={!cancelReason.trim()}>Cancel Request</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </AppShell>
  );
}



function ApproversPanel({ req, requestNumber, user, onReload, canEdit, submitReq, fmtTz }) {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  const loadPreview = useCallback(() => {
    setLoadingPreview(true);
    api.get(`/api/approvals/preview/${requestNumber}?_t=${Date.now()}`)
      .then(r => setPreview(r.data))
      .catch(() => setPreview({ matched_rules: [], approval_chain: [], total_rules: 0, total_approvers: 0 }))
      .finally(() => setLoadingPreview(false));
  }, [requestNumber]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const recalculate = async () => {
    setRecalculating(true);
    try {
      const r = await api.post(`/api/approvals/recalculate/${requestNumber}`);
      toast.success(r.data.message);
      onReload();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Recalculation failed')); }
    setRecalculating(false);
  };

  const chain = req.approval_chain || [];
  const routing = req.approval_routing_method || (preview?.matched_rules?.[0]?.routing_method) || 'sequential';
  const matchedRules = preview?.matched_rules || [];

  return (
    <div>
      <h3 className="font-bold text-primary mb-3">Approvers</h3>

      {/* Stale approval alert */}
      {req.approval_needs_recalc && (
        <div className="mb-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-300">
          <p className="text-sm font-semibold text-amber-700">Approvals may need recalculating</p>
          <p className="text-xs text-amber-600 mt-1">The requisition lines have been modified since the approvals were last calculated. The approval chain may no longer be accurate.</p>
          <Button size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700" onClick={recalculate} disabled={recalculating}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${recalculating ? 'animate-spin' : ''}`} /> {recalculating ? 'Recalculating...' : 'Recalculate Approvals'}
          </Button>
        </div>
      )}

      {req.rejection_notes && (
        <div className="mb-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200">
          <p className="text-sm font-semibold text-red-700">Rejected by {req.rejected_by}</p>
          <p className="text-xs text-red-600 mt-1">{req.rejection_notes}</p>
        </div>
      )}

      {/* Matching Approval Rules — always shown, loaded dynamically */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Matching Approval Rules (by rank)</p>
          <button onClick={loadPreview} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="refresh-rules-btn">
            <RefreshCw className="h-3 w-3" /> Re-check Rules
          </button>
        </div>
        {loadingPreview ? (
          <p className="text-xs text-muted-foreground py-3 text-center">Analyzing rules...</p>
        ) : matchedRules.length === 0 ? (
          <div className="border border-amber-200 rounded-lg bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-700">No approval rules match this requisition</p>
            <p className="text-xs text-amber-600 mt-1">If approvals are required for requisitions, this document cannot proceed until matching rules are configured in System Administration.</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            {matchedRules.sort((a, b) => a.rank - b.rank).map((r, i) => (
              <RuleRow key={i} rule={r} />
            ))}
          </div>
        )}
      </div>

      {/* Notification Method */}
      {(chain.length > 0 || (preview?.total_approvers || 0) > 0) && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200">
          <p className="text-xs">
            <span className="font-semibold text-blue-700">Notification Method: </span>
            <span className="text-blue-600">
              {routing === 'sequential' ? 'Sequential — approvers notified one after another in order' :
               routing === 'parallel' ? 'All at Once — all approvers notified simultaneously, all must approve' :
               'Any One — all notified simultaneously, first to respond decides'}
            </span>
          </p>
        </div>
      )}

      {/* Approver Chain */}
      {chain.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Approvers ({chain.length})</p>
          {chain.map((a, i) => {
            const isWaiting = a.status === 'WAITING';
            const isMe = a.user_id === user?.user_id;
            const canAction = isWaiting && isMe;
            const isOOO = a.is_ooo;
            return (
              <div key={i} className={`p-3 border rounded-xl ${isWaiting ? 'border-primary/40 bg-primary/5' : ''} ${a.status === 'CANCELLED' ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center ${
                    a.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                    a.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                    a.status === 'CANCELLED' ? 'bg-gray-100 text-gray-400' :
                    isWaiting ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                  }`}>{a.step || i+1}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium ${a.status === 'CANCELLED' ? 'line-through' : ''} ${isOOO ? 'line-through text-muted-foreground' : ''}`}>{a.name}</p>
                      {isOOO && <span className="text-[9px] bg-amber-100 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5 font-semibold">OOO</span>}
                    </div>
                    <p className="text-xs text-muted-foreground">{a.email} &middot; {a.matched_rules ? a.matched_rules.join(', ') : a.rule_name}</p>
                    {isOOO && a.delegate_name && (
                      <p className="text-xs text-amber-600 mt-0.5">Delegated to <strong>{a.delegate_name}</strong></p>
                    )}
                  </div>
                  <Badge variant="outline" className={`text-xs ${
                    a.status === 'APPROVED' ? 'bg-green-50 text-green-700 border-green-200' :
                    a.status === 'REJECTED' ? 'bg-red-50 text-red-700 border-red-200' :
                    a.status === 'CANCELLED' ? 'bg-gray-50 text-gray-400 border-gray-200' :
                    isWaiting ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    'bg-amber-50 text-amber-700 border-amber-200'
                  }`}>{a.status}</Badge>
                </div>
                {a.notes && <p className="text-xs text-muted-foreground mt-2 pl-10">{a.notes}</p>}
                {a.actioned_at && <p className="text-[10px] text-muted-foreground mt-1 pl-10">{fmtTz(a.actioned_at)}</p>}
                {canAction && <ApproveRejectActions requestNumber={requestNumber} onDone={onReload} />}
              </div>
            );
          })}
        </div>
      ) : preview && preview.total_approvers > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Approvers that will be notified ({preview.total_approvers})</p>
          {preview.approval_chain.map((a, i) => (
            <div key={i} className="p-3 border rounded-xl border-dashed">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-muted text-muted-foreground text-xs font-bold flex items-center justify-center">{a.step}</div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{a.name}</p>
                  <p className="text-xs text-muted-foreground">{a.email} &middot; {a.matched_rules ? a.matched_rules.join(', ') : a.rule_name}</p>
                </div>
                <Badge variant="outline" className="text-xs text-muted-foreground">Will be notified</Badge>
              </div>
            </div>
          ))}
          <p className="text-[10px] text-muted-foreground">Submit the requisition to activate the approval workflow.</p>
        </div>
      ) : !loadingPreview && matchedRules.length > 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">Submit the requisition to assign approvers.</p>
      ) : null}

      {/* Submit for Approval button — only if rules match */}
      {canEdit && !loadingPreview && matchedRules.length > 0 && (
        <div className="mt-4 pt-3 border-t">
          <Button size="sm" onClick={submitReq} className="bg-green-600 hover:bg-green-700">Submit for Approval</Button>
        </div>
      )}
    </div>
  );
}



function RuleRow({ rule: r }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border-b last:border-0 bg-card">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent/30 transition-colors text-left">
        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center flex-shrink-0">#{r.rank}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{r.rule_name}</p>
          <p className="text-[10px] text-muted-foreground">{r.rule_description || `${r.criteria?.length || 0} criteria`}</p>
        </div>
        <Badge variant="outline" className="text-[10px] capitalize">{(r.routing_method || 'sequential').replace('_', ' ')}</Badge>
        <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="px-4 pb-3 pt-1">
          {(r.criteria || []).length === 0 ? (
            <p className="text-[10px] text-muted-foreground italic">No criteria — matches all documents</p>
          ) : (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Criteria</p>
              {r.criteria.map((c, j) => (
                <div key={j} className="flex items-center gap-2 text-xs bg-muted/30 rounded px-2 py-1">
                  <span className="font-medium text-primary">{c.field}</span>
                  <span className="text-muted-foreground font-mono">{c.operator}</span>
                  <span className="font-semibold">{c.value}</span>
                </div>
              ))}
            </div>
          )}
          {(r.approvers || []).length > 0 && (
            <div className="mt-2 space-y-1">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Approvers in this rule</p>
              {r.approvers.map((a, j) => (
                <p key={j} className="text-xs text-muted-foreground">{typeof a === 'string' ? a : a.name || a.user_id}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function ApproveRejectActions({ requestNumber, onDone }) {
  const [mode, setMode] = useState(null); // 'approve' | 'reject'
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAction = async () => {
    setSubmitting(true);
    try {
      if (mode === 'approve') {
        await api.post(`/api/approvals/approve/${requestNumber}`, { notes });
        toast.success('Approved');
      } else {
        if (!notes.trim()) { toast.error('Please provide a reason for rejection'); setSubmitting(false); return; }
        await api.post(`/api/approvals/reject/${requestNumber}`, { notes });
        toast.success('Rejected — returned to originator');
      }
      setMode(null);
      setNotes('');
      onDone();
    } catch (e) {
      const msg = getApiErrorMessage(e, 'Action failed');
      if (msg.startsWith('Unable to approve')) toast.warning(msg);
      else toast.error(msg);
    }
    setSubmitting(false);
  };

  if (!mode) {
    return (
      <div className="flex gap-2 mt-3 pl-10">
        <Button size="sm" onClick={() => setMode('approve')} className="bg-green-600 hover:bg-green-700" data-testid="approve-btn">
          <CheckSquare className="h-3.5 w-3.5 mr-1" /> Approve
        </Button>
        <Button size="sm" variant="destructive" onClick={() => setMode('reject')} data-testid="reject-btn">
          <X className="h-3.5 w-3.5 mr-1" /> Reject
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-3 pl-10 space-y-2">
      <p className="text-xs font-semibold">{mode === 'approve' ? 'Approval Notes (optional)' : 'Reason for Rejection *'}</p>
      <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder={mode === 'reject' ? 'Please explain why...' : 'Optional notes...'} className="text-xs" data-testid="approval-notes" />
      <div className="flex gap-2">
        <Button size="sm" onClick={handleAction} disabled={submitting}
          className={mode === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
          variant={mode === 'reject' ? 'destructive' : 'default'}
          data-testid="confirm-approval-action">
          {submitting ? 'Processing...' : mode === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setMode(null); setNotes(''); }}>Cancel</Button>
      </div>
    </div>
  );
}


// ── Inventory Check Panel ─────────────────────────────────────────────────────
function InventoryCheckPanel({ lineItems, requestNumber, onReload }) {
  const [stockCheck, setStockCheck] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [fulfilling, setFulfilling] = React.useState(false);
  const [selectedFulfilments, setSelectedFulfilments] = React.useState({});

  const runCheck = async () => {
    setLoading(true);
    try {
      const items = lineItems.filter(li => li.item_code).map(li => ({
        item_code: li.item_code, quantity: li.quantity || 1,
      }));
      if (items.length === 0) { toast.info('No items with item codes to check'); setLoading(false); return; }
      const r = await api.post('/api/inventory/requisition-stock-check', { line_items: items });
      setStockCheck(r.data.results || []);
      // Pre-select suggested fulfilments
      const selections = {};
      for (const item of (r.data.results || [])) {
        if (item.is_stock_item && item.qty_from_stock > 0) {
          for (const loc of item.locations) {
            if (loc.suggested_issue > 0) {
              selections[`${item.item_code}|${loc.location_code}`] = {
                item_code: item.item_code, location_code: loc.location_code,
                quantity: loc.suggested_issue, selected: true,
              };
            }
          }
        }
      }
      setSelectedFulfilments(selections);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Stock check failed')); }
    setLoading(false);
  };

  React.useEffect(() => { if (lineItems.length > 0) runCheck(); }, []); // eslint-disable-line

  const toggleFulfilment = (key) => {
    setSelectedFulfilments(prev => ({
      ...prev,
      [key]: { ...prev[key], selected: !prev[key]?.selected },
    }));
  };

  const updateFulfilQty = (key, qty) => {
    setSelectedFulfilments(prev => ({
      ...prev,
      [key]: { ...prev[key], quantity: parseFloat(qty) || 0 },
    }));
  };

  const executeFulfilment = async () => {
    const items = Object.values(selectedFulfilments).filter(f => f.selected && f.quantity > 0);
    if (items.length === 0) { toast.error('No items selected for fulfilment'); return; }
    setFulfilling(true);
    try {
      const r = await api.post('/api/inventory/requisition-fulfil', {
        requisition_number: requestNumber,
        fulfilments: items.map(f => ({ item_code: f.item_code, location_code: f.location_code, quantity: f.quantity })),
      });
      toast.success(r.data.message);
      runCheck(); // Refresh
      onReload();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Fulfilment failed')); }
    setFulfilling(false);
  };

  const fmtNum = (v) => (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  const stockItems = (stockCheck || []).filter(r => r.is_stock_item);
  const nonStockItems = (stockCheck || []).filter(r => !r.is_stock_item);
  const hasStockItems = stockItems.length > 0;
  const totalFromStock = stockItems.reduce((s, r) => s + r.qty_from_stock, 0);
  const totalToOrder = stockItems.reduce((s, r) => s + r.qty_to_order, 0);
  const totalReplenish = stockItems.reduce((s, r) => s + r.replenishment_qty, 0);

  return (
    <div data-testid="inventory-check-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-primary text-sm">Inventory Stock Check</h3>
        <Button size="sm" variant="outline" onClick={runCheck} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Checking...' : 'Re-check Stock'}
        </Button>
      </div>

      {!stockCheck && !loading && (
        <div className="text-center py-8 text-muted-foreground text-sm">
          Click "Re-check Stock" to see inventory availability for this requisition.
        </div>
      )}

      {stockCheck && (
        <div>
          {/* Summary banner */}
          {hasStockItems && (
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="border rounded-lg p-3 bg-green-50">
                <p className="text-[10px] text-green-600 font-semibold">Fulfil from Stock</p>
                <p className="text-lg font-bold text-green-700">{fmtNum(totalFromStock)}</p>
              </div>
              <div className="border rounded-lg p-3 bg-blue-50">
                <p className="text-[10px] text-blue-600 font-semibold">Order Shortfall</p>
                <p className="text-lg font-bold text-blue-700">{fmtNum(totalToOrder)}</p>
              </div>
              <div className="border rounded-lg p-3 bg-amber-50">
                <p className="text-[10px] text-amber-600 font-semibold">Replenish to Min</p>
                <p className="text-lg font-bold text-amber-700">{fmtNum(totalReplenish)}</p>
              </div>
              <div className="border rounded-lg p-3 bg-purple-50">
                <p className="text-[10px] text-purple-600 font-semibold">Total to Order</p>
                <p className="text-lg font-bold text-purple-700">{fmtNum(totalToOrder + totalReplenish)}</p>
              </div>
            </div>
          )}

          {/* Stock items detail */}
          {stockItems.map(item => (
            <div key={item.item_code} className="border rounded-xl p-4 mb-3" data-testid={`stock-check-${item.item_code}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="font-semibold text-sm text-primary">{item.item_code}</span>
                  <span className="text-xs text-muted-foreground ml-2">{item.description}</span>
                </div>
                <Badge className={`text-[9px] ${item.qty_from_stock >= item.qty_needed ? 'bg-green-100 text-green-700' : item.qty_from_stock > 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                  {item.qty_from_stock >= item.qty_needed ? 'Fully Available' : item.qty_from_stock > 0 ? 'Partial Stock' : 'No Stock'}
                </Badge>
              </div>

              <div className="grid grid-cols-5 gap-2 text-xs mb-3 bg-muted/30 rounded-lg p-2">
                <div><span className="text-muted-foreground">Needed:</span> <strong>{fmtNum(item.qty_needed)}</strong></div>
                <div><span className="text-muted-foreground">Available:</span> <strong className="text-green-700">{fmtNum(item.total_available)}</strong></div>
                <div><span className="text-muted-foreground">From Stock:</span> <strong className="text-green-700">{fmtNum(item.qty_from_stock)}</strong></div>
                <div><span className="text-muted-foreground">To Order:</span> <strong className="text-blue-700">{fmtNum(item.qty_to_order)}</strong></div>
                <div><span className="text-muted-foreground">Replenish:</span> <strong className="text-amber-700">{fmtNum(item.replenishment_qty)}</strong></div>
              </div>

              {/* Location breakdown with fulfilment controls */}
              <table className="w-full text-[11px] border-collapse">
                <thead><tr className="border-b bg-muted/50">
                  <th className="text-left px-2 py-1.5">Location</th>
                  <th className="text-right px-2 py-1.5">In Stock</th>
                  <th className="text-right px-2 py-1.5">Reserved</th>
                  <th className="text-right px-2 py-1.5">Quarantined</th>
                  <th className="text-right px-2 py-1.5">Available</th>
                  <th className="text-right px-2 py-1.5">Min Level</th>
                  <th className="text-right px-2 py-1.5">Issue Qty</th>
                  <th className="text-center px-2 py-1.5 w-8">Fulfil</th>
                </tr></thead>
                <tbody>
                  {item.locations.map(loc => {
                    const key = `${item.item_code}|${loc.location_code}`;
                    const sel = selectedFulfilments[key];
                    const afterIssue = loc.current_stock - (sel?.selected ? (sel?.quantity || 0) : 0);
                    const belowMin = afterIssue < loc.minimum_stock_level && loc.minimum_stock_level > 0;
                    return (
                      <tr key={loc.location_code} className={`border-b ${belowMin ? 'bg-amber-50' : ''}`}>
                        <td className="px-2 py-1.5 font-semibold">{loc.location_code} <span className="font-normal text-muted-foreground">{loc.bin_number ? `(${loc.bin_number})` : ''}</span></td>
                        <td className="px-2 py-1.5 text-right">{fmtNum(loc.current_stock)}</td>
                        <td className="px-2 py-1.5 text-right text-purple-600">{fmtNum(loc.reserved)}</td>
                        <td className="px-2 py-1.5 text-right text-red-600">{fmtNum(loc.quarantined)}</td>
                        <td className="px-2 py-1.5 text-right font-semibold text-green-700">{fmtNum(loc.available)}</td>
                        <td className="px-2 py-1.5 text-right text-muted-foreground">{fmtNum(loc.minimum_stock_level)}</td>
                        <td className="px-2 py-1.5 text-right">
                          {loc.available > 0 && (
                            <input type="number" step="0.01" min="0" max={loc.available}
                              value={sel?.quantity || 0}
                              onChange={e => updateFulfilQty(key, e.target.value)}
                              className="w-16 h-6 text-[11px] text-right rounded border px-1" />
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {loc.available > 0 && (
                            <input type="checkbox" checked={sel?.selected || false} onChange={() => toggleFulfilment(key)} className="h-3.5 w-3.5" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Warning if fulfilment will drop below minimum */}
              {item.locations.some(loc => {
                const key = `${item.item_code}|${loc.location_code}`;
                const sel = selectedFulfilments[key];
                const afterIssue = loc.current_stock - (sel?.selected ? (sel?.quantity || 0) : 0);
                return afterIssue < loc.minimum_stock_level && loc.minimum_stock_level > 0 && sel?.selected;
              }) && (
                <div className="mt-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  Fulfilling from stock will drop below minimum level. A replenishment order of <strong>{fmtNum(item.replenishment_qty)}</strong> units is recommended to restore minimum levels.
                  {item.qty_to_order > 0 && <span> Plus <strong>{fmtNum(item.qty_to_order)}</strong> units to cover the shortfall. <strong>Total to order: {fmtNum(item.total_order_qty)}</strong>.</span>}
                </div>
              )}
            </div>
          ))}

          {/* Non-stock items */}
          {nonStockItems.length > 0 && (
            <div className="border rounded-xl p-3 mb-3 bg-muted/20">
              <p className="text-xs font-semibold text-muted-foreground mb-2">Non-Inventoried Items (order in full)</p>
              {nonStockItems.map(item => (
                <div key={item.item_code} className="flex items-center justify-between text-xs py-1">
                  <span>{item.item_code} — {item.description}</span>
                  <span className="text-muted-foreground">Order: {fmtNum(item.qty_needed)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Fulfil button */}
          {hasStockItems && totalFromStock > 0 && (
            <div className="flex items-center gap-3 mt-4 p-3 border rounded-xl bg-green-50">
              <div className="flex-1">
                <p className="text-xs font-semibold text-green-800">Ready to fulfil from inventory</p>
                <p className="text-[10px] text-green-600">
                  {Object.values(selectedFulfilments).filter(f => f.selected && f.quantity > 0).length} location(s) selected,{' '}
                  {fmtNum(Object.values(selectedFulfilments).filter(f => f.selected).reduce((s, f) => s + (f.quantity || 0), 0))} units total
                </p>
              </div>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={executeFulfilment} disabled={fulfilling} data-testid="fulfil-from-inventory-btn">
                <ArrowDownToLine className="h-3.5 w-3.5 mr-1" /> {fulfilling ? 'Fulfilling...' : 'Fulfil from Inventory'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
